import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { contactsApi } from '../../../api/contacts'
import { RefusedError } from '../../../api/client'
import type { CardSite, InCommon } from '../../../types/contacts'
import { FactRow } from './FactRow'
import { addLabel, inCommonWord, siteTitles, sourceLine } from './words'

// One site on the card: its title, the API's note when it has one, the fact rows, and "Add what you know" at
// the foot. The connections site lists what is in common instead of facts. The style site's facts are the
// counts the API measured, rendered as they came — never a trait word.

export function SitePanel({ site, inCommon, crmConnected, spoke, personId, onChanged }: {
  site: CardSite; inCommon: InCommon[]; crmConnected: boolean; spoke: string; personId: string; onChanged: () => void
}) {
  const [value, setValue] = useState('')
  const [personal, setPersonal] = useState(false)
  // "not a coincidence" (PL-218): the same word in two unrelated sentences is nothing; the item comes off and stays off
  const dismiss = useMutation({
    mutationFn: (c: InCommon) => contactsApi.dismissInCommon(spoke, personId, { kind: c.kind, id: c.id ?? '', label: c.label }),
    onSuccess: () => onChanged(),
  })
  const say = useMutation({
    mutationFn: () => contactsApi.sayFact(spoke, personId, { site: site.site, value: value.trim(), sensitivity: personal ? 'personal' : undefined }),
    onSuccess: () => { setValue(''); setPersonal(false); onChanged() },
  })
  const label = addLabel(site.site)
  const connections = site.site === 'connections'
  // the history site says where the CRM stands until its connector lands: the note leads the panel
  const noteFirst = site.note && (site.site !== 'history' || !crmConnected)

  return (
    <section className={`card site-panel site-${site.site}`} data-site={site.site}>
      <div className="card-head"><h2>{siteTitles[site.site]}</h2></div>
      {noteFirst && <p className="muted small site-note">{site.note}</p>}

      {connections ? (
        inCommon.length === 0 ? <p className="muted small">Nothing in common on file yet.</p> : (
          <ul className="plain fact-list">
            {inCommon.map((c, i) => (
              <li key={`${c.kind}-${c.id ?? i}`} className={`fact-row in-common kind-${c.kind}`}>
                <div className="fact-main">
                  <div className="fact-line">
                    <span className={`tag ${c.kind === 'coincidence' ? 'in-common' : 'shared'}`}>{inCommonWord(c.kind)}</span>
                    <span className="fact-value">{c.label}</span>
                    {c.onlyFor && <span className="muted small only-for">between the two of you</span>}
                    {(c.kind === 'coincidence' || c.kind === 'mention') && c.id && (
                      <button type="button" className="link-button small fact-dismiss" onClick={() => dismiss.mutate(c)} disabled={dismiss.isPending} title="The same word in two unrelated sentences: take it off the card. It stays off, kept as a 'not right' row you can take back.">Not a coincidence</button>
                    )}
                  </div>
                  {c.sources.length > 0 && <p className="muted small fact-sources">{sourceLine(c.sources)}</p>}
                </div>
              </li>
            ))}
          </ul>
        )
      ) : site.facts.length === 0 ? (
        <p className="muted small">Nothing on file yet.</p>
      ) : (
        <ul className="plain fact-list">
          {site.facts.map((f) => <FactRow key={f.assertionId} fact={f} spoke={spoke} personId={personId} onChanged={onChanged} />)}
        </ul>
      )}

      {site.note && !noteFirst && <p className="muted small site-note">{site.note}</p>}

      <form className="site-add" onSubmit={(e) => { e.preventDefault(); if (value.trim()) say.mutate() }}>
        <input type="text" value={value} onChange={(e) => setValue(e.target.value)} placeholder={label} aria-label={label} disabled={say.isPending} />
        <label className="check"><input type="checkbox" checked={personal} onChange={(e) => setPersonal(e.target.checked)} disabled={say.isPending} /> personal — mine only</label>
        <button className="button small primary" type="submit" disabled={!value.trim() || say.isPending}>{say.isPending ? 'Adding…' : 'Add'}</button>
      </form>
      {say.isError && <p className="error small">{say.error instanceof RefusedError ? say.error.refusal.message : say.error.message}</p>}
      {dismiss.isError && <p className="error small">{dismiss.error instanceof RefusedError ? dismiss.error.refusal.message : dismiss.error.message}</p>}
    </section>
  )
}
